import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "../../interfaces/DecisionPriority";
import { Board, Move, IPiece, Position, PieceType } from "../../../../types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  clonePieceWithPrototype,
} from "../../../utils/MCTSUtils";
import { MCTSMoveGenerationService } from "../../../utils/MCTSMoveGenerationService";

/**
 * Priority 5: Race Disrupt
 *
 * If an enemy king can win via a specific movement pattern via ability, the AI must disrupt this
 * This is specific to:
 * Monkey leap chains - the AI must move a piece out of the leap chain to no longer allow the monkey to leap over the set of pieces
 * Goat Traverse - the AI must move a piece to disconnect the traverse path of the goat if it can stop an immediate race victory
 * Rhino charge - the AI must not move a piece in front of the rhino OR must remove a piece from being infront of the rhino if this allows the rhino to win via charge
 * Lone Wolf - the AI must not capture a piece if it allows the Wolf to win via lone wolf movement on the next turn!
 */
export class RaceDisruptPriority extends BaseDecisionPriority {
  readonly priority = 5;
  readonly name = "Race Disrupt";
  readonly description = "Disrupt enemy race patterns that can lead to victory";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    return true; // Always relevant - need to check for pattern disruption opportunities
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];
    this.addLog("🎯 RACE DISRUPT: Analyzing movement pattern disruption...");

    const gameState = context.gameState;
    const enemyColor = gameState.isLightTurn ? "black" : "white";

    try {
      // FIRST: Check if there's actually an immediate race threat to disrupt
      const immediateRaceThreats = this.findImmediateRaceThreats(
        gameState,
        enemyColor
      );

      if (immediateRaceThreats.length === 0) {
        this.addLog(
          "❌ No immediate race threats detected - Race Disrupt not needed"
        );
        return {
          shouldEarlyExit: false,
          reasoning:
            "No immediate race threats to disrupt (enemy not close to victory)",
          priority: this.priority,
          diagnostics: {
            logs: this.analysisLogs,
          },
        };
      }

      this.addLog(
        `🚨 Found ${immediateRaceThreats.length} immediate race threats - analyzing disruption...`
      );

      // NEW LOGIC: Check if we have pieces already in defensive blocking positions
      const blockingAnalysis = this.analyzeCurrentBlockingPositions(
        context.allPossibleMoves,
        gameState,
        enemyColor
      );

      if (blockingAnalysis.isAlreadyBlocking) {
        this.addLog(
          `🛡️ DEFENSIVE POSITION DETECTED: ${blockingAnalysis.explanation}`
        );

        // CRITICAL SAFETY CHECK: Don't filter ALL moves unless there's a REAL immediate threat
        if (
          blockingAnalysis.allMovesEnable &&
          immediateRaceThreats.length > 0
        ) {
          this.addLog(
            `💀 POSITION ANALYSIS: All available moves enable enemy victory!`
          );
          this.addLog(
            `🚨 CRITICAL: We must maintain defensive blocking positions!`
          );

          // Look for any non-blocking piece moves
          const safeAlternativeMoves = this.findSafeAlternativeMoves(
            context.allPossibleMoves,
            gameState,
            enemyColor
          );

          if (safeAlternativeMoves.length > 0) {
            this.addLog(
              `✅ Found ${safeAlternativeMoves.length} safe alternative moves (non-blocking pieces)`
            );
            return {
              shouldEarlyExit: false,
              reasoning: `DEFENSIVE FILTERING: Restricted to ${safeAlternativeMoves.length} safe moves that don't enable enemy race`,
              priority: this.priority,
              filteredMoves: safeAlternativeMoves,
              diagnostics: {
                logs: this.analysisLogs,
                safeMovesFound: safeAlternativeMoves.length,
              },
            };
          } else {
            this.addLog(`💀 NO SAFE MOVES FOUND - POSITION IS LOST!`);
            this.addLog(
              `🚨 All pieces that can move would enable immediate enemy victory!`
            );
            this.addLog(
              `⚠️ SAFETY FALLBACK: Allowing original moves rather than crash - position assessment may be wrong`
            );

            // SAFETY FALLBACK: Rather than crash the system, allow the original moves
            // This prevents infinite loops and system crashes
            return {
              shouldEarlyExit: false,
              reasoning:
                "SAFETY FALLBACK: Race Disrupt detected critical position but allowing moves to prevent system crash",
              priority: this.priority,
              filteredMoves: context.allPossibleMoves, // Return original moves as fallback
              diagnostics: {
                logs: this.analysisLogs,
                fallbackActivated: true,
                originalMoveCount: context.allPossibleMoves.length,
              },
            };
          }
        } else {
          this.addLog(
            `✅ DEFENSIVE ANALYSIS: Blocking positions detected but not all moves are dangerous`
          );
          this.addLog(
            `🎯 CONTINUING: Will check for specific disruption opportunities`
          );
        }
      }

      // ORIGINAL LOGIC: Find all race disruption opportunities
      const disruptionMoves = this.findRaceDisruptionMoves(
        context.allPossibleMoves,
        gameState,
        enemyColor
      );

      if (disruptionMoves.length > 0) {
        this.addLog(`🛡️ Found ${disruptionMoves.length} race disruption moves`);
        this.addLog(
          `🎯 FILTERING to disruption moves only - let MCTS choose the best one!`
        );
        this.addLog(
          `🔧 Race Disrupt filtered moves: ${context.allPossibleMoves.length} -> ${disruptionMoves.length}`
        );

        return {
          shouldEarlyExit: false,
          reasoning: `RACE DISRUPTION: Filtered to ${disruptionMoves.length} moves that disrupt immediate race threats`,
          priority: this.priority,
          filteredMoves: disruptionMoves,
          diagnostics: {
            logs: this.analysisLogs,
            disruptionMovesFound: disruptionMoves.length,
          },
        };
      }

      this.addLog("❌ No race disruption opportunities found");
      return {
        shouldEarlyExit: false,
        reasoning: "No race disruption opportunities found",
        priority: this.priority,
        diagnostics: {
          logs: this.analysisLogs,
        },
      };
    } catch (error) {
      this.addLog(`❌ RACE DISRUPT ERROR: ${error}`);
      this.addLog(
        `❌ Stack trace: ${error instanceof Error ? error.stack : "No stack"}`
      );

      return {
        shouldEarlyExit: false,
        reasoning: `Race Disrupt analysis failed: ${error}`,
        priority: this.priority,
        diagnostics: {
          logs: this.analysisLogs,
        },
      };
    }
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  /**
   * NEW METHOD: Analyze if we currently have pieces in defensive blocking positions
   * that are preventing immediate enemy victory
   */
  private analyzeCurrentBlockingPositions(
    allMoves: MoveInfo[],
    gameState: any,
    enemyColor: "white" | "black"
  ): {
    isAlreadyBlocking: boolean;
    allMovesEnable: boolean;
    explanation: string;
    blockingPieces: Position[];
  } {
    const blockingPieces: Position[] = [];
    let movesEnablingCount = 0;

    // Check if any of our moves would enable immediate enemy race threats
    for (const moveInfo of allMoves) {
      const wouldEnable = this.wouldMoveEnableAnyRaceThreat(
        moveInfo,
        gameState,
        enemyColor
      );
      if (wouldEnable) {
        movesEnablingCount++;

        // Track which pieces are in blocking positions
        const blockingPos = moveInfo.fromPosition;
        if (
          !blockingPieces.some(
            (pos) => pos.row === blockingPos.row && pos.col === blockingPos.col
          )
        ) {
          blockingPieces.push(blockingPos);
        }
      }
    }

    const allMovesEnable =
      movesEnablingCount === allMoves.length && allMoves.length > 0;
    const isAlreadyBlocking = blockingPieces.length > 0;

    let explanation = "";
    if (isAlreadyBlocking) {
      explanation = `${blockingPieces.length} piece(s) currently blocking enemy race paths. ${movesEnablingCount}/${allMoves.length} moves would enable enemy victory.`;
    }

    return {
      isAlreadyBlocking,
      allMovesEnable,
      explanation,
      blockingPieces,
    };
  }

  /**
   * NEW METHOD: Find moves by pieces that are NOT currently in blocking positions
   */
  private findSafeAlternativeMoves(
    allMoves: MoveInfo[],
    gameState: any,
    enemyColor: "white" | "black"
  ): MoveInfo[] {
    const safeMoves: MoveInfo[] = [];

    for (const moveInfo of allMoves) {
      // Check if this move would enable any race threat
      if (!this.wouldMoveEnableAnyRaceThreat(moveInfo, gameState, enemyColor)) {
        this.addLog(
          `✅ SAFE MOVE: [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}] does not enable enemy race`
        );
        safeMoves.push(moveInfo);
      }
    }

    return safeMoves;
  }

  /**
   * NEW METHOD: Check if a move would enable ANY immediate race threat
   */
  private wouldMoveEnableAnyRaceThreat(
    moveInfo: MoveInfo,
    gameState: any,
    enemyColor: "white" | "black"
  ): boolean {
    const victoryRow = enemyColor === "white" ? 7 : 0;

    // EARLY GAME SAFETY CHECK: Don't be overly paranoid in the first few turns
    if (gameState.turnCount && gameState.turnCount <= 3) {
      this.addLog(
        `🛡️ EARLY GAME SAFETY: Turn ${gameState.turnCount} - being less restrictive about threat detection`
      );
      // In early game, only worry about VERY obvious immediate threats
    }

    // Check against all enemy race threats
    // For now, focus on Rhino charges (most common immediate threat)
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = this.getPieceAt(gameState.board, { row, col });
        if (
          piece &&
          piece.isKing &&
          piece.isLight === (enemyColor === "white") &&
          piece.type === "Rhino"
        ) {
          // DEBUG: Log what we're checking
          this.addLog(
            `🔍 Checking if move [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}] enables Rhino at [${row},${col}]`
          );

          // Check if this move would enable this Rhino's charge
          if (
            this.wouldMoveEnableRhinoCharge(
              moveInfo,
              gameState,
              { row, col },
              victoryRow
            )
          ) {
            this.addLog(`⚠️ ENABLER DETECTED: Move enables Rhino charge!`);
            return true;
          }
        }
      }
    }

    // TODO: Add checks for other race patterns (Monkey leaps, Goat traversal, etc.)
    this.addLog(`✅ SAFE: Move does not enable any race threats`);
    return false;
  }

  private findImmediateRaceThreats(
    gameState: any,
    enemyColor: "white" | "black"
  ): any[] {
    // Check if enemy has any 1-turn race threats (can win on their next move)
    const originalTurn = gameState.isLightTurn;
    gameState.isLightTurn = enemyColor === "white";

    try {
      const moveService = MCTSMoveGenerationService.getInstance();
      const enemyMoves = moveService.getAllMoves(gameState);
      const victoryRow = enemyColor === "white" ? 7 : 0;
      const threats = [];

      this.addLog(
        `🔍 THREAT DETECTION: Checking ${enemyMoves.length} enemy ${enemyColor} moves for immediate race threats to row ${victoryRow}`
      );

      for (const moveInfo of enemyMoves) {
        const move = moveInfo.move;
        const piece = this.getPieceAt(gameState.board, moveInfo.fromPosition);

        if (piece && piece.isKing && move.position.row === victoryRow) {
          this.addLog(
            `🚨 IMMEDIATE RACE THREAT: ${piece.type} at [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] can reach victory [${move.position.row},${move.position.col}]`
          );
          threats.push({
            piece,
            position: moveInfo.fromPosition,
            victorySquare: move.position,
            moveInfo,
          });
        }
      }

      this.addLog(`📊 Found ${threats.length} immediate (1-turn) race threats`);

      // DEBUG: If no threats found but it's early game, that's expected
      if (
        threats.length === 0 &&
        gameState.turnCount &&
        gameState.turnCount <= 5
      ) {
        this.addLog(
          `✅ EARLY GAME CONFIRMATION: No immediate threats on turn ${gameState.turnCount} is normal`
        );
      }

      return threats;
    } finally {
      gameState.isLightTurn = originalTurn;
    }
  }

  private findRaceDisruptionMoves(
    allMoves: MoveInfo[],
    gameState: any,
    enemyColor: "white" | "black"
  ): MoveInfo[] {
    const disruptionMoves: MoveInfo[] = [];
    this.addLog(
      `🔍 Checking ${allMoves.length} moves for race disruption potential...`
    );

    // Check each of our possible moves for disruption potential
    for (const moveInfo of allMoves) {
      try {
        if (this.doesMoveDisruptRacePattern(moveInfo, gameState, enemyColor)) {
          this.addLog(
            `✅ DISRUPTION FOUND: [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}]`
          );
          disruptionMoves.push(moveInfo);
        }
      } catch (error) {
        this.addLog(
          `⚠️ Error checking move [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}]: ${error}`
        );
      }
    }

    this.addLog(
      `📊 Found ${disruptionMoves.length} disruption moves out of ${allMoves.length} total`
    );
    return disruptionMoves;
  }

  private doesMoveDisruptRacePattern(
    moveInfo: MoveInfo,
    gameState: any,
    enemyColor: "white" | "black"
  ): boolean {
    // SIMPLIFIED APPROACH: Focus on specific disruption patterns
    this.addLog(
      `🔍 Checking disruption for move [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}]`
    );

    // 1. Check if this move disrupts monkey leap chains
    if (this.doesMoveDisruptMonkeyLeap(moveInfo, gameState, enemyColor)) {
      this.addLog(`🐒 MONKEY LEAP DISRUPTION DETECTED!`);
      return true;
    }

    // 2. Check if this move disrupts goat traversal
    if (this.doesMoveDisruptGoatTraversal(moveInfo, gameState, enemyColor)) {
      this.addLog(`🐐 GOAT TRAVERSAL DISRUPTION DETECTED!`);
      return true;
    }

    // 3. Check if this move disrupts rhino charge patterns
    const rhinoDisruption = this.doesMoveBlockRhinoCharge(
      moveInfo,
      gameState,
      enemyColor
    );
    if (rhinoDisruption === "blocks") {
      this.addLog(`🦏 RHINO CHARGE DISRUPTION DETECTED - BLOCKS CHARGE!`);
      return true;
    } else if (rhinoDisruption === "enables") {
      this.addLog(`🚨 RHINO CHARGE ENABLER DETECTED - FILTERING OUT!`);
      return false; // This move enables enemy charge - bad!
    }

    return false;
  }

  private doesMoveDisruptMonkeyLeap(
    moveInfo: MoveInfo,
    gameState: any,
    enemyColor: "white" | "black"
  ): boolean {
    // CRITICAL: Only activate for 1-turn race threats (immediate victory next turn)
    const victoryRow = enemyColor === "white" ? 7 : 0;

    // First, check if enemy actually has a 1-turn race threat
    if (!this.hasImmediateRaceThreat(gameState, enemyColor)) {
      this.addLog(
        "❌ No immediate (1-turn) race threat found - Race Disrupt not needed"
      );
      return false;
    }

    // Find enemy monkey kings that can win in 1 turn
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const piece = this.getPieceAt(gameState.board, { row, col });
        if (
          piece &&
          piece.isKing &&
          piece.isLight === (enemyColor === "white") &&
          piece.type === "Monkey"
        ) {
          // Check if this monkey has a 1-turn race threat
          if (
            this.canMonkeyWinInOneTurn(
              gameState,
              { row, col },
              victoryRow,
              enemyColor
            )
          ) {
            this.addLog(
              `🚨 IMMEDIATE THREAT: Enemy monkey king at [${row},${col}] can win in 1 turn!`
            );
            // Check if our move could disrupt this monkey's leap path
            if (
              this.wouldMoveDisruptMonkeyPath(
                moveInfo,
                { row, col },
                victoryRow
              )
            ) {
              this.addLog(
                `🎯 Our move would disrupt 1-turn monkey leap threat!`
              );
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  private hasImmediateRaceThreat(
    gameState: any,
    enemyColor: "white" | "black"
  ): boolean {
    // Check if enemy can win on their next move
    const originalTurn = gameState.isLightTurn;
    gameState.isLightTurn = enemyColor === "white";

    try {
      const moveService = MCTSMoveGenerationService.getInstance();
      const enemyMoves = moveService.getAllMoves(gameState);
      const victoryRow = enemyColor === "white" ? 7 : 0;

      for (const moveInfo of enemyMoves) {
        const move = moveInfo.move;
        const piece = this.getPieceAt(gameState.board, moveInfo.fromPosition);

        if (piece && piece.isKing && move.position.row === victoryRow) {
          this.addLog(
            `🚨 IMMEDIATE RACE THREAT: ${piece.type} at [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] can reach victory [${move.position.row},${move.position.col}]`
          );
          return true;
        }
      }

      return false;
    } finally {
      gameState.isLightTurn = originalTurn;
    }
  }

  private canMonkeyWinInOneTurn(
    gameState: any,
    monkeyPos: Position,
    victoryRow: number,
    enemyColor: "white" | "black"
  ): boolean {
    // Check if this specific monkey can reach victory row in 1 turn
    const originalTurn = gameState.isLightTurn;
    gameState.isLightTurn = enemyColor === "white";

    try {
      const moveService = MCTSMoveGenerationService.getInstance();
      const enemyMoves = moveService.getAllMoves(gameState);

      for (const moveInfo of enemyMoves) {
        if (
          moveInfo.fromPosition.row === monkeyPos.row &&
          moveInfo.fromPosition.col === monkeyPos.col &&
          moveInfo.move.position.row === victoryRow
        ) {
          return true;
        }
      }

      return false;
    } finally {
      gameState.isLightTurn = originalTurn;
    }
  }

  private wouldMoveDisruptMonkeyPath(
    moveInfo: MoveInfo,
    monkeyPos: Position,
    victoryRow: number
  ): boolean {
    const ourDestination = moveInfo.move.position;

    // Simple heuristic: if we're moving to a square that's:
    // 1. Between the monkey and victory row
    // 2. In a position that could be part of a leap chain

    const monkeyToVictoryDirection = victoryRow > monkeyPos.row ? 1 : -1;

    // Check if our destination is between monkey and victory
    const isBetweenRows =
      (monkeyToVictoryDirection > 0 &&
        ourDestination.row > monkeyPos.row &&
        ourDestination.row < victoryRow) ||
      (monkeyToVictoryDirection < 0 &&
        ourDestination.row < monkeyPos.row &&
        ourDestination.row > victoryRow);

    // Check if it's in a reasonable column range (monkey can leap diagonally)
    const isInReasonableColumn =
      Math.abs(ourDestination.col - monkeyPos.col) <= 2;

    this.addLog(
      `📊 Monkey at [${monkeyPos.row},${monkeyPos.col}], our dest [${ourDestination.row},${ourDestination.col}]: between=${isBetweenRows}, column=${isInReasonableColumn}`
    );

    return isBetweenRows && isInReasonableColumn;
  }

  private doesMoveDisruptGoatTraversal(
    moveInfo: MoveInfo,
    gameState: any,
    enemyColor: "white" | "black"
  ): boolean {
    // Similar logic for goat traversal disruption
    // For now, return false to focus on monkey leap
    return false;
  }

  private doesMoveBlockRhinoCharge(
    moveInfo: MoveInfo,
    gameState: any,
    enemyColor: "white" | "black"
  ): "blocks" | "enables" | "none" {
    const victoryRow = enemyColor === "white" ? 7 : 0;

    // CRITICAL: Only activate for immediate 1-turn Rhino King charge threats
    this.addLog(
      `🦏 RHINO CHARGE CHECK: Looking for enemy ${enemyColor} Rhino Kings that can charge to victory...`
    );

    // Find enemy Rhino Kings
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = this.getPieceAt(gameState.board, { row, col });
        if (
          piece &&
          piece.isKing &&
          piece.isLight === (enemyColor === "white") &&
          piece.type === "Rhino"
        ) {
          this.addLog(
            `🦏 Found enemy Rhino King at [${row},${col}] - checking charge potential...`
          );

          // Check if this Rhino can charge to victory row immediately
          if (
            this.canRhinoChargeToVictory(
              gameState,
              { row, col },
              victoryRow,
              enemyColor
            )
          ) {
            this.addLog(
              `🚨 CRITICAL: Rhino King at [${row},${col}] can charge to victory row ${victoryRow}!`
            );

            // Check if our move would ENABLE this charge by moving a blocking piece
            if (
              this.wouldMoveEnableRhinoCharge(
                moveInfo,
                gameState,
                { row, col },
                victoryRow
              )
            ) {
              this.addLog(
                `⚠️ DANGER: Our move [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}] would ENABLE Rhino charge!`
              );
              return "enables"; // This move ENABLES the charge
            }

            // Check if our move would BLOCK this charge by staying in the path
            if (
              this.wouldMoveBlockRhinoCharge(
                moveInfo,
                gameState,
                { row, col },
                victoryRow
              )
            ) {
              this.addLog(
                `🛡️ BLOCK: Our move [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}] would BLOCK Rhino charge!`
              );
              return "blocks"; // This move blocks the charge - good disruption
            }
          }
        }
      }
    }

    return "none"; // No Rhino charge threats found or no disruption potential
  }

  /**
   * Check if a Rhino King can use its charge ability to reach victory row immediately
   */
  private canRhinoChargeToVictory(
    gameState: any,
    rhinoPos: Position,
    victoryRow: number,
    enemyColor: "white" | "black"
  ): boolean {
    const originalTurn = gameState.isLightTurn;
    gameState.isLightTurn = enemyColor === "white";

    try {
      const moveService = MCTSMoveGenerationService.getInstance();
      const rhinoMoves = moveService.getAllMoves(gameState);

      // Filter to only moves from this Rhino's position
      const rhinoSpecificMoves = rhinoMoves.filter(
        (move) =>
          move.fromPosition.row === rhinoPos.row &&
          move.fromPosition.col === rhinoPos.col &&
          move.move.position.row === victoryRow
      );

      this.addLog(
        `🦏 Rhino at [${rhinoPos.row},${rhinoPos.col}] has ${rhinoSpecificMoves.length} moves to victory row ${victoryRow}`
      );

      // Check if any move is a charge (3+ squares with pieces in path)
      for (const moveInfo of rhinoSpecificMoves) {
        const distance = Math.abs(moveInfo.move.position.row - rhinoPos.row);
        if (distance >= 3) {
          this.addLog(
            `🎯 CHARGE DETECTED: Rhino can charge ${distance} squares to [${moveInfo.move.position.row},${moveInfo.move.position.col}]!`
          );
          return true;
        }
      }

      return false;
    } finally {
      gameState.isLightTurn = originalTurn;
    }
  }

  /**
   * Check if our move would ENABLE enemy Rhino charge by removing a blocking piece
   */
  private wouldMoveEnableRhinoCharge(
    moveInfo: MoveInfo,
    gameState: any,
    rhinoPos: Position,
    victoryRow: number
  ): boolean {
    // Check if we're moving a piece that's currently blocking the Rhino's charge path
    const chargePath = this.getRhinoChargePath(rhinoPos, victoryRow);

    for (const pathSquare of chargePath) {
      // If our piece is currently at this path square and we're moving it away
      if (
        moveInfo.fromPosition.row === pathSquare.row &&
        moveInfo.fromPosition.col === pathSquare.col
      ) {
        this.addLog(
          `🚨 ENABLER: Moving piece from [${pathSquare.row},${pathSquare.col}] - this blocks Rhino charge path!`
        );
        return true;
      }
    }

    return false;
  }

  /**
   * Check if our move would BLOCK enemy Rhino charge by placing a piece in the path
   */
  private wouldMoveBlockRhinoCharge(
    moveInfo: MoveInfo,
    gameState: any,
    rhinoPos: Position,
    victoryRow: number
  ): boolean {
    // Check if we're moving a piece INTO the Rhino's charge path
    const chargePath = this.getRhinoChargePath(rhinoPos, victoryRow);

    for (const pathSquare of chargePath) {
      // If we're moving TO this path square
      if (
        moveInfo.move.position.row === pathSquare.row &&
        moveInfo.move.position.col === pathSquare.col
      ) {
        this.addLog(
          `🛡️ BLOCKER: Moving piece to [${pathSquare.row},${pathSquare.col}] - this blocks Rhino charge path!`
        );
        return true;
      }
    }

    return false;
  }

  /**
   * Get the squares in a Rhino's potential charge path to victory row
   */
  private getRhinoChargePath(
    rhinoPos: Position,
    victoryRow: number
  ): Position[] {
    const path: Position[] = [];
    const direction = victoryRow > rhinoPos.row ? 1 : -1; // Forward or backward

    // Rhino charges in straight line forward/backward
    for (
      let row = rhinoPos.row + direction;
      row !== victoryRow + direction;
      row += direction
    ) {
      if (row >= 0 && row < 8) {
        path.push({ row, col: rhinoPos.col });
      }
    }

    this.addLog(
      `🦏 Rhino charge path from [${rhinoPos.row},${
        rhinoPos.col
      }] to row ${victoryRow}: ${path
        .map((p) => `[${p.row},${p.col}]`)
        .join(", ")}`
    );

    return path;
  }

  private findRaceThreats(
    gameState: any,
    enemyColor: "white" | "black"
  ): Array<{
    piece: IPiece;
    position: Position;
    victorySquares: Position[];
    patternType: "monkey" | "goat" | "rhino" | "wolf";
  }> {
    const moveService = MCTSMoveGenerationService.getInstance();

    // Temporarily switch to enemy turn
    const originalTurn = gameState.isLightTurn;
    gameState.isLightTurn = enemyColor === "white";

    try {
      const enemyMoves = moveService.getAllMoves(gameState);
      return this.findRaceThreatsFromMoves(enemyMoves, gameState, enemyColor);
    } finally {
      gameState.isLightTurn = originalTurn;
    }
  }

  private findRaceThreatsFromMoves(
    enemyMoves: MoveInfo[],
    gameState: any,
    enemyColor: "white" | "black"
  ): Array<{
    piece: IPiece;
    position: Position;
    victorySquares: Position[];
    patternType: "monkey" | "goat" | "rhino" | "wolf";
  }> {
    const threats = [];
    const victoryRow = enemyColor === "white" ? 7 : 0;

    for (const moveInfo of enemyMoves) {
      const move = moveInfo.move;
      const piece = this.getPieceAt(gameState.board, moveInfo.fromPosition);

      if (!piece || !piece.isKing) continue;

      // Check if this move reaches victory
      if (move.position.row === victoryRow) {
        const patternType = this.determinePatternType(
          piece,
          moveInfo,
          gameState
        );
        if (patternType) {
          threats.push({
            piece,
            position: moveInfo.fromPosition,
            victorySquares: [move.position],
            patternType,
          });
        }
      }
    }

    return threats;
  }

  private determinePatternType(
    piece: IPiece,
    moveInfo: MoveInfo,
    gameState: any
  ): "monkey" | "goat" | "rhino" | "wolf" | null {
    const pieceType = piece.type.toLowerCase();
    const move = moveInfo.move;

    switch (pieceType) {
      case "monkey":
        // Check if this is a leap move (requires pieces to jump over)
        if (move.moveType?.toString() === "leap") {
          return "monkey";
        }
        break;

      case "goat":
        // Check if this move traverses over other pieces
        if (this.isGoatTraversalMove(moveInfo, gameState)) {
          return "goat";
        }
        break;

      case "rhino":
        // Check if this is a charge move (3+ squares in straight line)
        if (this.isRhinoChargeMove(moveInfo, gameState)) {
          return "rhino";
        }
        break;

      case "wolf":
        // Check if this uses lone wolf movement
        if (this.isLoneWolfMove(moveInfo, gameState)) {
          return "wolf";
        }
        break;
    }

    return null;
  }

  private isGoatTraversalMove(moveInfo: MoveInfo, gameState: any): boolean {
    // Check if goat is moving over other pieces to reach destination
    const start = moveInfo.fromPosition;
    const end = moveInfo.move.position;

    const dx = Math.sign(end.col - start.col);
    const dy = Math.sign(end.row - start.row);

    let currentPos = { row: start.row + dy, col: start.col + dx };

    // Check each square in the path
    while (currentPos.row !== end.row || currentPos.col !== end.col) {
      const square = gameState.board[currentPos.row][currentPos.col];
      if (square.Pieces.length > 0) {
        return true; // Goat is traversing over pieces
      }
      currentPos.row += dy;
      currentPos.col += dx;
    }

    return false;
  }

  private isRhinoChargeMove(moveInfo: MoveInfo, gameState: any): boolean {
    // Check if rhino is moving 3+ squares in straight line
    const start = moveInfo.fromPosition;
    const end = moveInfo.move.position;

    const distance =
      Math.abs(end.row - start.row) + Math.abs(end.col - start.col);

    // Rhino charge is 3+ squares
    return distance >= 3;
  }

  private isLoneWolfMove(moveInfo: MoveInfo, gameState: any): boolean {
    // Check if wolf has no pack members (alone) allowing extended movement
    const wolfPos = moveInfo.fromPosition;
    const wolf = this.getPieceAt(gameState.board, wolfPos);

    if (!wolf) return false;

    // Check surrounding squares for pack members
    const directions = [
      [-1, -1],
      [-1, 0],
      [-1, 1],
      [0, -1],
      [0, 1],
      [1, -1],
      [1, 0],
      [1, 1],
    ];

    for (const [dx, dy] of directions) {
      const checkPos = { row: wolfPos.row + dy, col: wolfPos.col + dx };
      if (this.isValidPosition(checkPos, gameState.board)) {
        const piece = this.getPieceAt(gameState.board, checkPos);
        if (
          piece &&
          piece.type === PieceType.Wolf &&
          piece.isLight === wolf.isLight
        ) {
          return false; // Has pack member, not lone wolf
        }
      }
    }

    return true; // No pack members found, lone wolf
  }

  private compareThreats(
    originalThreats: Array<{
      piece: IPiece;
      position: Position;
      victorySquares: Position[];
      patternType: "monkey" | "goat" | "rhino" | "wolf";
    }>,
    newThreats: Array<{
      piece: IPiece;
      position: Position;
      victorySquares: Position[];
      patternType: "monkey" | "goat" | "rhino" | "wolf";
    }>,
    moveInfo: MoveInfo
  ): boolean {
    // Check if we disrupted any threats
    for (const originalThreat of originalThreats) {
      const stillExists = newThreats.some(
        (newThreat) =>
          newThreat.piece.id === originalThreat.piece.id &&
          newThreat.patternType === originalThreat.patternType
      );

      if (!stillExists) {
        this.addLog(
          `🎯 PATTERN DISRUPTION: Move [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}] disrupts ${originalThreat.patternType} race pattern!`
        );
        return true;
      }
    }

    // Special case: Check if we're avoiding creating lone wolf scenario
    if (this.wouldCreateLoneWolf(moveInfo, originalThreats)) {
      this.addLog(
        `🐺 LONE WOLF PREVENTION: Avoiding move that would create lone wolf scenario`
      );
      return false; // This move would be BAD, don't include it
    }

    return false;
  }

  private wouldCreateLoneWolf(
    moveInfo: MoveInfo,
    originalThreats: Array<{
      piece: IPiece;
      position: Position;
      victorySquares: Position[];
      patternType: "monkey" | "goat" | "rhino" | "wolf";
    }>
  ): boolean {
    // Check if our move would capture a piece that prevents lone wolf
    const move = moveInfo.move;

    // If we're not capturing, can't create lone wolf
    if (!move.moveType || move.moveType.toString() !== "capture") {
      return false;
    }

    // Check if any wolf threats would become lone wolf after our capture
    for (const threat of originalThreats) {
      if (threat.patternType === "wolf") {
        // Check if our capture would isolate this wolf
        // This is a simplified check - in reality we'd need to check pack dynamics
        const capturePos = move.position;
        const wolfPos = threat.position;

        // If we're capturing near the wolf, it might become lone wolf
        const distance =
          Math.abs(capturePos.row - wolfPos.row) +
          Math.abs(capturePos.col - wolfPos.col);
        if (distance <= 2) {
          return true; // Might create lone wolf scenario
        }
      }
    }

    return false;
  }

  private simulateMove(gameState: any, moveInfo: MoveInfo): any {
    // Create a deep copy of the game state
    const testState = cloneFullGameState(gameState);

    // Apply the move
    const fromPos = moveInfo.fromPosition;
    const toPos = moveInfo.move.position;

    const fromSquare = testState.board[fromPos.row][fromPos.col];
    if (fromSquare.Pieces.length === 0) return testState;

    const piece = clonePieceWithPrototype(fromSquare.Pieces[0]);

    // Move the piece
    fromSquare.Pieces = [];
    testState.board[toPos.row][toPos.col].Pieces = [piece];

    return testState;
  }

  private getPieceAt(board: any[][], position: Position): IPiece | null {
    if (
      position.row < 0 ||
      position.row >= board.length ||
      position.col < 0 ||
      position.col >= board[0].length
    ) {
      return null;
    }

    const square = board[position.row][position.col];
    return square.Pieces.length > 0 ? square.Pieces[0] : null;
  }

  private isValidPosition(position: Position, board: any[][]): boolean {
    return (
      position.row >= 0 &&
      position.row < board.length &&
      position.col >= 0 &&
      position.col < board[0].length
    );
  }
}

